import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { useAvailability } from '@/features/calls/hooks/useAvailability';

import {
  getPublicProfessionals,
  getFeaturedProfessionals,
  getPublicProfessionalById,
  getSpecializations,
  type PublicProfessionalQueryParams,
} from '../services/professional-public.service';
import { sortFeaturedLive } from '../lib/featuredOrder';

export function usePublicProfessionals(params?: PublicProfessionalQueryParams) {
  return useQuery({
    queryKey: ['professionals', 'public', params],
    queryFn: () => getPublicProfessionals(params),
    placeholderData: keepPreviousData,
    // Dedupe rapid refetches (paging, page-size switches, remounts) so large /
    // "Tutti" listings don't flood the API. Pairs with the server-side TTL cache.
    staleTime: 30_000,
    // Anonymous visitors have no availability socket: the REST onlineStatus in
    // this payload is their only presence source, so keep it periodically fresh.
    refetchInterval: 60_000,
  });
}

export function useFeaturedProfessionals() {
  // Server order: online first, then by last connected call. The periodic
  // refetch keeps that base order fresh; the live re-sort below reacts
  // instantly when someone goes online/offline while the page is open.
  const query = useQuery({
    queryKey: ['professionals', 'featured'],
    queryFn: getFeaturedProfessionals,
    refetchInterval: 60_000,
  });
  const { getStatus } = useAvailability();
  const data = useMemo(
    () => (query.data ? sortFeaturedLive(query.data, getStatus) : query.data),
    [query.data, getStatus]
  );
  return { ...query, data };
}

export function usePublicProfessional(id: string) {
  return useQuery({
    queryKey: ['professionals', 'public', id],
    queryFn: () => getPublicProfessionalById(id),
    enabled: !!id,
    // Same as the listing: without a socket (anonymous), REST is the only
    // presence source for the profile page CTA.
    refetchInterval: 60_000,
  });
}

export function useSpecializations() {
  return useQuery({
    queryKey: ['professionals', 'specializations'],
    queryFn: getSpecializations,
  });
}
